import {Component, inject, OnDestroy, OnInit, signal, ViewChild, WritableSignal, ChangeDetectionStrategy} from '@angular/core';
import {CanvasComponent} from '@/app/pages/p-place/components/canvas/canvas.component';
import {Router} from '@angular/router';
import {Colour} from '@/app/core/models/p-place/colour';
import {ColourService} from '@services/p-place/colour.service';
import {DatePipe, NgClass} from '@angular/common';
import {PixelService} from '@services/p-place/pixel.service';
import {Pixel, PlaceTwitchUserContext} from '@/app/core/models/p-place/pixel';
import {interval, Subscription} from 'rxjs';
import {LeaderboardComponent} from '@/app/pages/p-place/components/leaderboard/leaderboard.component';
import {ChallengeComponent} from '@/app/pages/p-place/components/challenge/challenge.component';
import {ToastService} from '@services/toast.service';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {InformationComponent} from '@/app/pages/p-place/components/information/information.component';
import {AuthService} from '@services/auth.service';
import {FeatureFlagDirective} from '@/app/shared/directives/feature-flag.directive';
import {environment} from '@/environments/environment';

@Component({
	selector: 'app-p-place',
	imports: [
		CanvasComponent,
		NgClass,
		LeaderboardComponent,
		ChallengeComponent,
		InformationComponent,
		TranslatePipe,
		DatePipe,
		FeatureFlagDirective
	],
	templateUrl: './p-place.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './p-place.component.css'
})
export class PPlaceComponent implements OnInit, OnDestroy {

	private readonly router = inject(Router);
	private readonly colourService = inject(ColourService);
	private readonly pixelService = inject(PixelService);
	private readonly toastService = inject(ToastService);
	private readonly translate = inject(TranslateService);
	protected readonly authService = inject(AuthService);

	protected isShowingColoursSelector = signal(false);
	protected colours: WritableSignal<Colour[]> = signal([]);
	protected selectedColour: WritableSignal<Colour | null> = signal(null);
	protected selectedPixel: WritableSignal<Pixel | null> = signal(null);

	protected localPixelsAvailable = signal(0);
	protected placeTwitchUserContext: WritableSignal<PlaceTwitchUserContext | null> = signal(null);

	protected isLeaderboardModalOpen = signal(false);
	protected isChallengeModalOpen = signal(false);
	protected isInformationModalOpen = signal(false);

	private simulationSubscription?: Subscription;
private readonly COOLDOWN_MS = 15_000;

	private simulationStartTime = 0;
	private simulationStartCount = 0;

	protected localPixelsPlaced = 0;
	@ViewChild(CanvasComponent) canvas!: CanvasComponent;

	private isFirstVisit: boolean = true;

	ngOnInit() {
		this.loadColours();
		this.loadCharges();
		this.checkIfFirstVisit();
	}

	checkIfFirstVisit() {
		// `localStorage` n'existe pas côté serveur (SSR) : ngOnInit s'exécute
		// aussi pendant le rendu serveur, sans ce garde-fou l'accès y lève une
		// ReferenceError et fait échouer le rendu de la page.
		try {
			this.isFirstVisit = localStorage.getItem('place-first-visit') === null;
			if (this.isFirstVisit) {
				localStorage.setItem('place-first-visit', 'false');
			}
		} catch {
			this.isFirstVisit = false;
			return;
		}
		if (this.isFirstVisit) {
			setTimeout(() => {
				this.isFirstVisit = false;
				this.openInformationModal();
			}, 1000);
		}
	}

	ngOnDestroy() {
		this.stopSimulation();
	}

	toHome() {
		this.router.navigateByUrl('/');
	}

	loadColours() {
		this.colourService.getColours().subscribe(colours => {
			this.colours.set(colours);
		})
	}

	protected toggleColoursSelector() {
		this.isShowingColoursSelector.update(value => !value);
	}

	download() {
		this.canvas.download();
	}

	protected selectColor(colour: Colour) {
		this.selectedColour.set(colour);
	}

	protected handlePixelPlaced($event: number) {

		const available = this.localPixelsAvailable();
		if (available <= 0) {
			return;
		}

		this.localPixelsPlaced++;

		this.localPixelsAvailable.update(count => Math.max(0, count - 1));

	}

	protected handlePixelRemoved($event: number) {

		if (this.localPixelsPlaced > 0) {
			this.localPixelsPlaced--;

			const context = this.placeTwitchUserContext();
			if (context) {
				this.localPixelsAvailable.update(count => Math.min(context.chargesInfo.maxPixels, count + 1));
			}

		}
	}

	protected validPixels() {
		if (this.localPixelsPlaced === 0) {
			return;
		}

		this.canvas.validatePixels();
	}

	private loadCharges() {

		this.pixelService.getPlaceUserContext().subscribe({
			next: (context) => {

				this.placeTwitchUserContext.set(context);
				this.initSimulation(context.chargesInfo.availablePixels, context.chargesInfo.maxPixels);
			},
			error: (err) => {
				console.error('❌ Erreur lors du chargement des charges:', err);
			}
		});
	}

	/**
	 * ✅ Appelé après l'envoi au backend
	 */
	pixelsValidated(pixels: Pixel[]) {
		this.pixelService.consumePixels(pixels).subscribe({
			next: (context) => {
				console.log('✅ Réponse du backend:', context);
				this.toastService.show(this.translate.instant('page.place.pixels.validated', { count: pixels.length }), 'success');

				this.placeTwitchUserContext.set(context);

				this.initSimulation(context.chargesInfo.availablePixels, context.chargesInfo.maxPixels);

			},
			error: (error) => {
				console.error('❌ Erreur lors de la validation:', error);
				this.toastService.show(this.translate.instant('page.place.pixels.error'), 'error');

				if (error.status === 400) {
					this.loadCharges();
				}
			}
		});
	}

	/**
	 * ✅ Initialise la simulation avec les données du backend
	 */
	private initSimulation(availablePixels: number, maxPixels: number) {
		const context = this.placeTwitchUserContext();
		if (!context) return;

		const fractionalPart = availablePixels - Math.floor(availablePixels);
		const elapsedInCooldown = fractionalPart;
		const elapsedSeconds = elapsedInCooldown * (this.COOLDOWN_MS / 1000);
		const elapsedMs = elapsedSeconds * 1000;

		this.simulationStartTime = Date.now() - elapsedMs;

		this.simulationStartCount = Math.floor(availablePixels);

		this.localPixelsPlaced = 0;

		const availableLocally = Math.floor(availablePixels);
		this.localPixelsAvailable.set(Math.min(availableLocally, maxPixels));

		this.startSimulation();
	}

	private startSimulation() {
		this.stopSimulation();

		const context = this.placeTwitchUserContext();
		if (!context) {
			return;
		}

		if (context.chargesInfo.availablePixels >= context.chargesInfo.maxPixels) {
			this.localPixelsAvailable.set(context.chargesInfo.maxPixels);
			return;
		}

		this.simulationSubscription = interval(100).subscribe(() => {
			const currentContext = this.placeTwitchUserContext();
			if (!currentContext) return;

			const elapsedMs = Date.now() - this.simulationStartTime;
			const elapsedSeconds = elapsedMs / 1000;

			const cooldownSeconds = this.COOLDOWN_MS / 1000;
			const pixelsRegenerated = elapsedSeconds / cooldownSeconds;

			const currentCount = Math.min(
				currentContext.chargesInfo.maxPixels,
				this.simulationStartCount + pixelsRegenerated
			);

			const availableLocally = Math.floor(currentCount) - this.localPixelsPlaced;
			this.localPixelsAvailable.set(Math.max(0, availableLocally));

			if (currentCount >= currentContext.chargesInfo.maxPixels) {
				this.stopSimulation();
			}
		});
	}

	private stopSimulation() {
		if (this.simulationSubscription) {
			this.simulationSubscription.unsubscribe();
			this.simulationSubscription = undefined;
		}
	}

	/**
	 * ✅ Calcule le count actuel (avec décimales)
	 */
	private getCurrentCount(): number {
		const context = this.placeTwitchUserContext();
		if (!context) return 0;

		const elapsedMs = Date.now() - this.simulationStartTime;
		const elapsedSeconds = elapsedMs / 1000;
		const cooldownSeconds = this.COOLDOWN_MS / 1000; // 30
		const pixelsRegenerated = elapsedSeconds / cooldownSeconds;

		const currentCount = Math.min(
			context.chargesInfo.maxPixels,
			this.simulationStartCount + pixelsRegenerated
		);

		return currentCount;
	}

	/**
	 * ✅ Calcule le temps restant avant le prochain pixel (en millisecondes)
	 * Basé sur la partie décimale de availablePixels
	 */
	protected getTimeUntilNextPixel(): number {
		const context = this.placeTwitchUserContext();
		if (!context) return 0;

		const currentCount = this.getCurrentCount();

		if (currentCount >= context.chargesInfo.maxPixels) return 0;

		const fractionalPart = currentCount - Math.floor(currentCount);

		const remainingFraction = 1.0 - fractionalPart;
		const remainingMs = remainingFraction * this.COOLDOWN_MS;

		return Math.max(0, remainingMs);
	}

	/**
	 * ✅ Formate le temps restant en secondes
	 */
	protected formatTimeRemaining(): string {
		const ms = this.getTimeUntilNextPixel();

		if (isNaN(ms) || ms === 0) {
			return '(0:00)';
		}

		const seconds = Math.ceil(ms / 1000);
		return "(0:" + seconds+ ")";
	}

	protected openLeaderboardModal() {
		this.isLeaderboardModalOpen.set(true);
	}

	protected closeLeaderboardModal() {
		this.isLeaderboardModalOpen.set(false);
	}

	protected openInformationModal() {
		this.isInformationModalOpen.set(true);
	}

	protected closeInformationModal() {
		this.isInformationModalOpen.set(false);
	}

	protected openChallengeModal() {
		this.isChallengeModalOpen.set(true);
	}

	protected closeChallengeModal() {
		this.isChallengeModalOpen.set(false);
	}

	protected handlePixelSelected(event: Pixel) {
		this.selectedPixel.set(event);
	}

	loginWithTwitch() {
		window.location.href = `${environment.backendUrl}/auth/twitch/authorize?origin=main`;
	}
}
